import './firebase';
import './sentry';
import './airbridge';
import React, { Suspense } from 'react';
import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import App from './components/App/App';
import store from './redux';
import { Provider } from 'react-redux';
import { getPlatforms, IonApp, setupIonicReact } from '@ionic/react';
import mixpanel from 'mixpanel-browser';
import { uid } from 'uid';
import './tailwind.css';
import './index.scss';
import 'react-photo-view/dist/react-photo-view.css';
import { AgoraProvider } from './context/AgoraContext/AgoraContext';
import { LangsProvider } from './context/LangsContext';
import { IntercomPluginProvider } from './context/IntercomPluginContext';
import { environment } from './enviroments';
import { IonReactRouter } from '@ionic/react-router';
import { ToastContextProvider } from './context/ToastContext/ToastContext';

import './i18n';
import { appThemes, AuthProvider, ThemeProvider, VideoProvider } from '@mindly/ui-components';
import { AnalyticsProvider, getEnvironment, MixpanelService, StreamChatMessagesProvider } from './shared';
import { PermissionProvider } from './shared/context/PushNotificationsContext';
import { SignAgreementsProvider } from './context/SignAgreementsContext/SignAgreements.context';
import { TikTokService } from './shared/context/AnalyticsContext/services/TikTokService';
import { FirebaseAnalyticsService } from './shared/context/AnalyticsContext/services/FirebaseAnalytics';
import { FirebaseInitOptions } from '@capacitor-community/firebase-analytics';
import { initSafeAreaEnvironment } from './shared/utils/safeArea';
import { FacebookPixelService } from './shared/context/AnalyticsContext/services/FacebookPixelService';
import CrashScreen from './components/ErrorBoundary/CrashScreen';
import { StripePaymentProvider } from './shared/context/StripePaymentsContext/StripePaymentProvider';

/**
 * Setting for disabling back gesture for iOS
 */
setupIonicReact({
  swipeBackEnabled: false,
});

const mixpanelService = new MixpanelService(import.meta.env.VITE_MIXPANEL_API_KEY, false);
const tiktokService = new TikTokService(environment.TIK_TOK_PIXEL_ID, false);
const firebaseAnalyticsService = new FirebaseAnalyticsService(
  environment.FIREBASE_CONFIG as FirebaseInitOptions,
  false
);
const facebookPixelService = new FacebookPixelService(environment.FB_PIXEL_ID, false);

const gettrackingKey = () => {
  let trackingKey = localStorage.getItem('gb_tracking_key');
  if (!trackingKey) {
    trackingKey = uid();
    localStorage.setItem('gb_tracking_key', trackingKey);
  }
  return trackingKey;
};

const gb = new GrowthBook({
  apiHost: 'https://cdn.growthbook.io',
  clientKey: import.meta.env.VITE_GROWTHBOOK_KEY,
  enableDevMode: import.meta.env.VITE_GROWTHBOOK_ENABLE_DEV_MODE === 'true',
  debug: getEnvironment() !== 'production',
  subscribeToChanges: true,
  attributes: {
    platforms: getPlatforms(),
    trackingKey: gettrackingKey(),
  },
  trackingCallback: (experiment, result) => {
    mixpanel.track('$experiment_started', {
      'Experiment name': experiment.key,
      'Variant name': result.key,
      $source: 'growthbook',
    });
  },
});
gb.init();

(async () => {
  await initSafeAreaEnvironment();

  /**
   * Render an App component in the root
   */
  ReactDOM.render(
    <Sentry.ErrorBoundary fallback={<CrashScreen />}>
      <IonApp>
        <Suspense fallback={''}>
          <Provider store={store}>
            <AuthProvider errorLoggerFunction={(message) => Sentry.captureException(new Error(message))}>
              <AnalyticsProvider
                services={[mixpanelService, tiktokService, firebaseAnalyticsService, facebookPixelService]}
              >
                <IntercomPluginProvider appId={environment.INTERCOM.APP_ID}>
                  <PermissionProvider>
                    <SignAgreementsProvider>
                      <ThemeProvider appTheme={appThemes.client}>
                        <LangsProvider>
                          <GrowthBookProvider growthbook={gb}>
                            <ToastContextProvider>
                              <StripePaymentProvider>
                                <AgoraProvider>
                                  <IonReactRouter>
                                    <StreamChatMessagesProvider>
                                      <VideoProvider>
                                        <App />
                                      </VideoProvider>
                                    </StreamChatMessagesProvider>
                                  </IonReactRouter>
                                </AgoraProvider>
                              </StripePaymentProvider>
                            </ToastContextProvider>
                          </GrowthBookProvider>
                        </LangsProvider>
                      </ThemeProvider>
                    </SignAgreementsProvider>
                  </PermissionProvider>
                </IntercomPluginProvider>
              </AnalyticsProvider>
            </AuthProvider>
          </Provider>
        </Suspense>
      </IonApp>
    </Sentry.ErrorBoundary>,
    document.getElementById('root')
  );
})();
